import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Loader2, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { useDebounce } from '@/hooks/useDebounce'
import { sireneApi, type SireneCompany } from '@/services/sirene'
import { cn } from '@/lib/utils'

interface Props {
  onSelect: (company: SireneCompany) => void
  inputClassName?: string
}

// 2.4.3-003 INSEE SIRENE lookup. Triggered when the user types a company name /
// SIRET; on selection the parent pre-fills name/address/SIRET. Degrades to manual
// entry when INSEE is unreachable or returns no match (exact AC messages).
export function SireneLookupField({ onSelect, inputClassName }: Props) {
  const { t } = useTranslation('clients')
  const [term, setTerm] = useState('')
  const debounced = useDebounce(term, 400)

  const { data, isFetching, isError } = useQuery({
    queryKey: ['sirene', debounced],
    queryFn: () => sireneApi.search(debounced),
    enabled: debounced.trim().length >= 3,
  })

  const results = data?.data ?? []
  const showDropdown = debounced.trim().length >= 3

  return (
    <div className='relative'>
      <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#1E1E1E]' />
      <Input
        value={term}
        onChange={e => setTerm(e.target.value)}
        placeholder={t('sirene.searchPlaceholder')}
        className={cn(inputClassName, 'pl-9 pr-9')}
      />
      {isFetching && (
        <Loader2 className='absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground' />
      )}

      {showDropdown && !isFetching && (
        <div className='absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-border bg-popover shadow-md'>
          {isError && (
            <p className='px-3 py-2 text-sm text-muted-foreground'>
              {t('sirene.unavailable')}
            </p>
          )}
          {!isError && results.length === 0 && (
            <p className='px-3 py-2 text-sm text-muted-foreground'>
              {t('sirene.noMatch')}
            </p>
          )}
          {!isError &&
            results.map(c => (
              <button
                key={c.siret}
                type='button'
                onClick={() => {
                  onSelect(c)
                  setTerm('')
                }}
                className='flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground'
              >
                <span className='font-medium'>{c.name}</span>
                <span className='text-xs text-muted-foreground'>
                  {c.siret} {c.city ? `· ${c.city}` : ''}
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  )
}
